import React, { useEffect, useMemo, useRef, useState } from 'react'
// @ts-ignore no types
import analytics from '@adc/analytics'
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import _ from 'lodash'
import moment from 'moment'
import classnames from 'classnames/bind'
import type { Listing, Property, CurrentTargetAudience } from '@adc/sdk/types'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import Button, { ButtonColors } from '../../elementals/button'
import Icon from '../../elementals/icon'
import Text from '../../elementals/text'
import PropertySaveIndicator from '../propertySaveIndicator'
import ListingIndicator from '../../listing/listingIndicator'
import {
  CardImage,
  LeftSidePropertyValue,
  PropertyInfo,
  AddressInfoLine,
} from '../../listing/listingCard/cardParts'
import { AutomationTarget } from '../../../types'
import cls from './styles.scss'

const cx = classnames.bind(cls)

const AUCTION_DATE_FORMAT = 'MMM D h:mm a'

const SAVE_SPARKLE_COUNT = 6
// Save burst lifetime: the bulge runs 500ms and the sparkles 550ms with up
// to 80ms of stagger — unmount the burst after the longest one ends.
const SAVE_ANIMATION_TOTAL_MS = 700

interface PropertyCardBadge {
  title: string
  color?: string
  foregroundColor?: string
}

export type PropertyCardTrackingContext = Record<string, unknown> & {
  badges: string[]
}

/** Layout variants. Only horizontal exists today; future property-based
 * cards (e.g. vertical) extend this union. */
export type PropertyCardVariant = 'horizontal'

export interface PropertyCardProps extends AutomationTarget {
  property: Property
  /** Layout variant. Defaults to 'horizontal' (photo left, details middle,
   * actions right; stacks vertically on mobile). */
  variant?: PropertyCardVariant
  /** How the save control renders: bare heart icon or labeled button ("Save for Updates") */
  saveControl?: 'icon' | 'button'
  isSaved?: boolean
  /**
   * Renders the card in its hidden state: content greyed out (grayscale +
   * reduced opacity), a "Hidden" pill at the top-right corner, and every
   * interaction (card link, save, hide) blocked. Presentational only — the
   * consumer keeps the card in place and decides which properties are
   * hidden.
   */
  isHidden?: boolean
  onSaveToggle?: (
    property: Property,
    trackingContext: PropertyCardTrackingContext,
    /**
     * The DOM element wrapping the save control (labeled button or heart
     * icon), handed to the consumer so app-level effects (e.g. the Your
     * Feed flying heart, MERCH-182) can start from the control's on-screen
     * position. Always last and optional — existing consumers ignore it.
     */
    saveControlElement?: HTMLElement | null
  ) => Promise<void>
  onHide?: (
    property: Property,
    trackingContext: PropertyCardTrackingContext
  ) => void
  /** Optional footnote rendered at the bottom-right of the card (italic), e.g. "Based on your browsing activity" */
  footnote?: React.ReactNode
  /**
   * Optional previous price rendered strikethrough before the current price
   * (price drop). Presentational only: the consumer maps whatever field
   * carries it to this prop.
   */
  previousPrice?: number
  linkProps?: {
    href?: string
    onClick?: (trackingContext: PropertyCardTrackingContext) => void
    target?: string
    rel?: string
  }
  loading?: boolean
  featureFlags?: Record<string, unknown>
  viewingFrom?: CurrentTargetAudience
  className?: string
}

const defaultOnSaveToggle = async () => {}

/**
 * The exact shape of the listing-shaped shell rendered when a property has
 * no active listing: shared card parts read property data (summary, photos,
 * valuation) through `listing.primary_property`, and that is the only field
 * the shell really carries. Kept as an explicit type so the single cast to
 * `Listing` below documents the true runtime shape instead of letting a
 * fabricated full Listing circulate unnoticed.
 */
type PropertyOnlyListingShell = Pick<Listing, 'primary_property'>

/**
 * PropertyCard Component
 *
 * Generic property-based card, first of the Property-based card family.
 * Data is property-level: listing details (price, auction date, badges,
 * status) come from the property's current disposition sales cycle and
 * degrade gracefully when the property has no active listing.
 *
 * Horizontal variant: photo on the left, listing details in the middle
 * (price, stats, address, auction date, badges, asset type) and actions on
 * the right (save, hide) with an optional footnote. Stacks vertically on
 * mobile.
 */
const PropertyCard = ({
  property,
  variant = 'horizontal',
  saveControl = 'icon',
  isSaved,
  isHidden = false,
  onSaveToggle = defaultOnSaveToggle,
  onHide,
  footnote,
  previousPrice,
  linkProps = {},
  loading = false,
  featureFlags = {},
  viewingFrom = graph.enums.CurrentTargetAudience.Buyer,
  className,
  elmId = 'property_card',
}: PropertyCardProps) => {
  // A property may have zero active listings (e.g. between disposition
  // sales cycles); the card then renders property-level data only.
  const cycle = property.current_disposition_sales_cycle
  const listing = useMemo<Listing | undefined>(() => {
    const activeListings = cycle?.active_listings ?? []
    return (
      activeListings.find(
        activeListing =>
          String(activeListing.listing_id) === String(cycle?.current_listing_id)
      ) ?? activeListings[0]
    )
  }, [cycle])

  // Listing-shaped view for the shared card parts (image, price, property
  // info, badges). The property prop is ALWAYS the source for property-level
  // facts (summary, saved/new flags) — even when a listing exists, its
  // embedded primary_property copy is overridden so the same fact never has
  // two sources. The listing contributes listing-level fields only (price,
  // auction, badges, status, photos). Single deliberate cast on the
  // no-listing path: card parts are null-safe on every field other than
  // primary_property (see PropertyOnlyListingShell).
  const displayListing = useMemo<Listing>(() => {
    const shell: PropertyOnlyListingShell = { primary_property: property }
    return listing ? { ...listing, ...shell } : (shell as Listing)
  }, [listing, property])

  const badges = useMemo<PropertyCardBadge[]>(
    () =>
      (
        blueprint.listing.utils
          // @ts-ignore untyped blueprint util
          .calculatePropertyCardBadges(displayListing, viewingFrom) as {
          title: string
          color?: string
          foregroundColor?: string
        }[]
      ).map(badge => ({
        title: badge.title,
        color: badge.color,
        foregroundColor: badge.foregroundColor,
      })),
    [displayListing, viewingFrom]
  )

  const constructTrackingContext = (): PropertyCardTrackingContext => ({
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    ...analytics.standardTracking.listing(displayListing),
    badges: badges.map(badge => badge.title),
  })

  // Save micro-interaction (MERCH-182): bulge + sparkle burst on the save
  // control when the buyer saves — never on unsave. Keyed by a counter so a
  // later save remounts (and thereby restarts) the burst. The animation is
  // optimistic: it plays on the leading edge of the debounced save, so if
  // the save later fails the consumer reverts isSaved but the burst has
  // already played (accepted — matches the reference prototype; the
  // consumer surfaces the error).
  const [saveAnimationKey, setSaveAnimationKey] = useState(0)
  // The debounced closure below intentionally keeps isSaved out of its deps
  // (recreating the debounce on every optimistic toggle would reset the
  // window and let a quick second click double-fire), so it reads the
  // latest value through a ref instead.
  const isSavedRef = useRef(isSaved)
  isSavedRef.current = isSaved
  // Wraps whichever save control renders (button or icon variant).
  const saveControlRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (saveAnimationKey === 0) return undefined
    const timer = setTimeout(
      () => setSaveAnimationKey(0),
      SAVE_ANIMATION_TOTAL_MS
    )
    return () => clearTimeout(timer)
  }, [saveAnimationKey])

  const debouncedSaveToggle = useMemo(
    () =>
      _.debounce(
        (savedProperty: Property) => {
          if (!isSavedRef.current) {
            setSaveAnimationKey(previous => previous + 1)
          }
          onSaveToggle?.(
            savedProperty,
            constructTrackingContext(),
            saveControlRef.current
          ).catch(() => {})
        },
        500,
        { leading: true, trailing: false }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSaveToggle, property]
  )

  // isHidden guards: pointer-events only blocks the mouse — the controls
  // remain keyboard-focusable, so every handler bails out explicitly.
  const handleSaveToggle = () => {
    if (isHidden) return
    debouncedSaveToggle(property)
  }

  const handleHide = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (isHidden) return
    onHide?.(property, constructTrackingContext())
  }

  const handleCardClick = () => {
    if (isHidden) return
    linkProps.onClick?.(constructTrackingContext())
  }

  const stopPropagationClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const auctionStartDate = listing?.auction?.start_date
  const auctionDateLabel =
    !loading && auctionStartDate
      ? `Auction: ${moment(auctionStartDate).format(AUCTION_DATE_FORMAT)}`
      : null

  return (
    <div
      className={cx(
        'root',
        `root--${variant}`,
        { 'root--hidden': isHidden },
        className
      )}
      data-elm-id={elmId}
    >
      {isHidden && (
        <span
          data-elm-id={`${elmId}_hidden_badge`}
          className={cx('hidden-badge')}
        >
          Hidden
        </span>
      )}
      <a
        href={isHidden ? undefined : linkProps.href}
        aria-disabled={isHidden || undefined}
        className={cx('card-link')}
        onClick={handleCardClick}
        target={linkProps.target}
        rel={linkProps.rel}
      >
        <div className={cx('image-column')}>
          <CardImage
            listing={displayListing}
            isHorizontalView
            loading={loading}
            featureFlags={featureFlags}
            viewingFrom={viewingFrom}
            className={cx('card-image')}
          />
        </div>
        {!loading && (
          <div className={cx('content-column')}>
            <div className={cx('price-row')}>
              {previousPrice != null && (
                <span
                  data-elm-id={`${elmId}_previous_price`}
                  className={cx('previous-price')}
                >
                  {blueprint.utils.formatMoney(previousPrice, undefined, true)}
                </span>
              )}
              <LeftSidePropertyValue
                listing={displayListing}
                featureFlags={featureFlags}
              />
            </div>
            <PropertyInfo listing={displayListing} elmId={elmId} />
            {listing ? (
              <AddressInfoLine
                address={listing.formatted_address}
                elmId={elmId}
                className={cx('address-line')}
              />
            ) : (
              property.full_address && (
                <Text
                  elmId={`address_line_${elmId}`}
                  size='small'
                  className={cx('address-line')}
                >
                  {property.full_address}
                </Text>
              )
            )}
            {auctionDateLabel && (
              <Text
                elmId={`${elmId}_auction_date`}
                size='small'
                className={cx('auction-date')}
              >
                {auctionDateLabel}
              </Text>
            )}
            {badges.length > 0 && (
              <div className={cx('badges-row')}>
                {badges.map(badge => (
                  <span
                    key={badge.title}
                    data-elm-id={`${elmId}_badge`}
                    className={cx('badge-pill')}
                    style={{
                      color: badge.color,
                      borderColor: badge.color,
                    }}
                  >
                    {badge.title}
                  </span>
                ))}
              </div>
            )}
            {listing && (
              <ListingIndicator
                listing={listing}
                elmId={elmId}
                hideAuctionDate
                hideAuctionType
                className={cx('asset-info')}
              />
            )}
          </div>
        )}
        {!loading && (
          <div className={cx('actions-column')}>
            <div className={cx('actions')}>
              <span
                ref={saveControlRef}
                onClick={
                  saveControl === 'icon' ? stopPropagationClick : undefined
                }
                className={cx('save-control', {
                  'save-control--bulge': saveAnimationKey > 0,
                })}
              >
                {saveControl === 'icon' && (
                  <PropertySaveIndicator
                    elmId={elmId}
                    isSaved={isSaved}
                    onSaveToggle={handleSaveToggle}
                    property={property}
                    whiteBackground
                  />
                )}
                {saveControl === 'button' && (
                  <Button
                    elmId={`${elmId}_save_button`}
                    role='secondary'
                    color={isSaved ? ButtonColors.Error : ButtonColors.Gray}
                    size='medium'
                    onClick={(event: React.MouseEvent) => {
                      stopPropagationClick(event)
                      handleSaveToggle()
                    }}
                  >
                    <Icon
                      name='heart'
                      iconStyle={isSaved ? 'solid' : 'regular'}
                      color='inherit'
                      className={cx('save-button-icon')}
                    />
                    {isSaved ? 'Saved' : 'Save for Updates'}
                  </Button>
                )}
                {saveAnimationKey > 0 && (
                  <span
                    key={saveAnimationKey}
                    className={cx('save-sparkles')}
                    aria-hidden='true'
                  >
                    {_.times(SAVE_SPARKLE_COUNT, index => (
                      <i key={index} />
                    ))}
                  </span>
                )}
              </span>
              {onHide && (
                <button
                  type='button'
                  data-elm-id={`${elmId}_hide`}
                  className={cx('hide-button')}
                  onClick={handleHide}
                >
                  <Icon name='eye-slash' iconStyle='regular' />
                  <span className={cx('hide-label')}>Hide</span>
                </button>
              )}
            </div>
            {footnote && (
              <Text
                elmId={`${elmId}_footnote`}
                size='tiny'
                color='grayMedium'
                className={cx('footnote')}
              >
                {footnote}
              </Text>
            )}
          </div>
        )}
      </a>
    </div>
  )
}

// @ts-ignore next line
PropertyCard[GRAPH_API_FRAGMENT_DEFINITION] =
  blueprint.property.fragments.propertyCard.definition

// @ts-ignore next line
PropertyCard[GRAPH_API_FRAGMENT_REFERENCE] =
  blueprint.property.fragments.propertyCard.reference

export default PropertyCard
